import {
  TextField,
  Page,
  Table,
  Button,
  Badge,
  Input,
  Checkbox,
  Modal,
  FormStack,
  Alert,
  FilterField,
  FilterPanel,
} from "@marekl/adminer/components";
import { useState } from "react";
import { Plus } from "lucide-react";
import { Topbar } from "@marekl/adminer/shell";
import { useDebouncedSearchFilter } from "@marekl/adminer/hooks";
import { useAuth } from "../../auth/useAuth";
import {
  useCategoryList,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
  type Category,
  type CategoryInput,
} from "../../lib/queries/categories";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; category: Category }
  | { kind: "delete"; category: Category };

export function CategoriesPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "manager" || user?.role === "superadmin";
  const [q, setQ] = useState("");
  const { draft: qDraft, setDraft: setQDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: setQ,
  });
  const { data, isLoading } = useCategoryList({ q });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const close = () => setModal({ kind: "closed" });

  return (
    <>
      <Topbar crumbs={[{ label: "Kategorie" }]} />
      <Page.Content stack gap="lg">
        <Page.Header
          title="Kategorie"
          subtitle="správa kategorií položek"
          actions={
            canEdit && (
              <Button variant="primary" onClick={() => setModal({ kind: "create" })}>
                <Plus size={16} aria-hidden />
                Přidat kategorii
              </Button>
            )
          }
        />

        <FilterPanel>
          <FilterField wide>
            <Input
              label="Hledat"
              type="search"
              value={qDraft}
              placeholder="název nebo popis"
              onChange={(e) => setQDraft(e.target.value)}
            />
          </FilterField>
        </FilterPanel>

        <Page.SectionHeader
          title="Kategorie"
          meta={total > 0 ? `${items.length} z ${total}` : undefined}
        />

        <Table<Category>
          rowKey={(c) => c.id}
          rows={items}
          empty={isLoading ? "Načítám…" : "Žádné kategorie."}
          columns={[
            { key: "name", header: "Název", render: (c) => c.name },
            { key: "description", header: "Popis", render: (c) => <TextField record={c} source="description" /> },
            {
              key: "active",
              header: "Stav",
              render: (c) =>
                c.active ? (
                  <Badge variant="ok">Aktivní</Badge>
                ) : (
                  <Badge variant="muted">Skrytá</Badge>
                ),
            },
            ...(canEdit
              ? [
                  {
                    key: "actions",
                    header: "",
                    render: (c: Category) => (
                      <Table.Actions>
                        <Table.Action onClick={() => setModal({ kind: "edit", category: c })}>
                          Upravit
                        </Table.Action>
                        <Table.Action onClick={() => setModal({ kind: "delete", category: c })}>
                          Smazat
                        </Table.Action>
                      </Table.Actions>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Page.Content>

      {modal.kind === "create" && <CategoryFormModal onClose={close} />}
      {modal.kind === "edit" && (
        <CategoryFormModal initial={modal.category} onClose={close} />
      )}
      {modal.kind === "delete" && (
        <DeleteCategoryModal category={modal.category} onClose={close} />
      )}
    </>
  );
}

interface FormProps {
  initial?: Category;
  onClose: () => void;
}

function CategoryFormModal({ initial, onClose }: FormProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [active, setActive] = useState(initial?.active ?? true);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateCategory();
  const update = useUpdateCategory();
  const isEdit = !!initial;
  const isPending = create.isPending || update.isPending;

  async function submit() {
    setError(null);
    const input: CategoryInput = { name, description, active };
    try {
      if (isEdit) {
        await update.mutateAsync({ id: initial!.id, ...input });
      } else {
        await create.mutateAsync(input);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Uložení se nezdařilo");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Upravit kategorii" : "Nová kategorie"}
      footer={
        <>
          <Button variant="default" onClick={onClose}>
            Zrušit
          </Button>
          <Button variant="primary" onClick={submit} disabled={isPending || !name.trim()}>
            Uložit
          </Button>
        </>
      }
    >
      <FormStack>
        {error && <Alert variant="err">{error}</Alert>}
        <Input
          label="Název"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Input
          label="Popis"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Checkbox
          label="Aktivní"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />
      </FormStack>
    </Modal>
  );
}

function DeleteCategoryModal({ category, onClose }: { category: Category; onClose: () => void }) {
  const del = useDeleteCategory();
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      await del.mutateAsync(category.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Smazání se nezdařilo");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Smazat kategorii"
      footer={
        <>
          <Button variant="default" onClick={onClose}>
            Zrušit
          </Button>
          <Button variant="danger" onClick={submit} disabled={del.isPending}>
            Smazat
          </Button>
        </>
      }
    >
      <FormStack>
        {error && <Alert variant="err">{error}</Alert>}
        <p>
          Opravdu chcete smazat kategorii <strong>{category.name}</strong>?
        </p>
      </FormStack>
    </Modal>
  );
}
